import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Alert, Button, Group, Loader, Pagination, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconFileTypePdf, IconPlus } from '@tabler/icons-react';

import { useGetPatientDocumentsQuery } from '@/store/slices/api/patient/patientDocumentsApiSlice';

import DocumentCard from './DocumentCard';
import styles from './Documents.module.scss';
import DocumentUploadModal from './DocumentUploadModal';

const Documents = () => {
  const [
    documentUploadModalOpened,
    { open: openDocumentUploadModal, close: closeDocumentUploadModal }
  ] = useDisclosure(false);
  const { uuid: patientId } = useParams() as { uuid: string };
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const {
    data: documentsData,
    error: documentsError,
    isLoading: documentsLoading,
    refetch: refetchDocuments
  } = useGetPatientDocumentsQuery(
    { patientId: patientId!, params: { limit, page } },
    { skip: !patientId }
  );

  const handleUploadSuccess = useCallback(() => {
    closeDocumentUploadModal();
    notifications.show({
      title: 'Success',
      message: 'Document uploaded successfully',
      color: 'green'
    });
  }, [closeDocumentUploadModal]);

  if (!patientId) {
    return <div className={styles.noPatient}>No Patient associated with this report.</div>;
  }

  if (documentsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Loader size="md" />
          <Text size="sm" c="dimmed" mt="md">
            Loading documents...
          </Text>
        </div>
      </div>
    );
  }

  if (documentsError) {
    return (
      <div className={styles.container}>
        <Alert color="red" title="Failed to load documents. Please try again." variant="light">
          <Button variant="light" size="xs" mt="sm" onClick={() => refetchDocuments()}>
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  const documents = documentsData?.data || [];
  const hasNoDocuments = documents.length === 0;

  return (
    <Stack gap="sm" className={styles.container}>
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {hasNoDocuments ? 'No documents' : `${documentsData?.total ?? documents.length} documents`}
        </Text>
        <Button
          size="xs"
          onClick={openDocumentUploadModal}
          variant="light"
          leftSection={<IconPlus size={16} />}>
          Add Document
        </Button>
      </Group>

      {hasNoDocuments ? (
        <Stack align="center" gap={4} py="xl">
          <IconFileTypePdf size={28} stroke={1.5} color="var(--mantine-color-dimmed)" />
          <Text size="sm" fw={500} c="dimmed">
            No documents uploaded
          </Text>
          <Text size="xs" c="dimmed">
            PDFs stored on this patient. Upload adds one here.
          </Text>
        </Stack>
      ) : (
        <Stack gap={8}>
          {documents.map((doc) => (
            <DocumentCard key={doc.id} document={doc} patientId={patientId} />
          ))}
          {documentsData && documentsData.totalPages > 1 && (
            <Group justify="center" mt="xs">
              <Pagination
                total={documentsData.totalPages}
                value={page}
                onChange={setPage}
                size="sm"
                color="plum"
              />
            </Group>
          )}
        </Stack>
      )}

      <DocumentUploadModal
        isOpen={documentUploadModalOpened}
        onClose={closeDocumentUploadModal}
        onUploadSuccess={handleUploadSuccess}
        patientId={patientId}
      />
    </Stack>
  );
};

export default Documents;
